import Flickity from 'flickity-imagesloaded';

const {registerModule} = window;

class Carousel {
  constructor(element) {
    this.element = element;
    this.initialize();
  }

  initialize() {
    this.bindUI();
    this.initCarousel();

    setTimeout(() => {
      this.flkty.resize();
    }, 600);

    if (this.ui.$items.length > 0) {
      this.bindEvents();
    }
  }

  unmount() {
    this.flkty.destroy();
  }

  bindUI() {
    this.ui = {};
    this.ui.$carousel = this.element.querySelector(
      '[data-ref="carousel-wrapper"]',
    );
    this.ui.$items = this.ui.$carousel.querySelectorAll(
      '[data-ref="carousel-item"]',
    );
    this.ui.$arrows = [
      ...this.element.querySelectorAll('[data-ref="carousel-arrow"]'),
    ];
    this.ui.$arrowPrev = this.element.querySelector('[data-direction="prev"]');
    this.ui.$arrowNext = this.element.querySelector('[data-direction="next"]');
    this.ui.position = this.element.getAttribute('data-position');
    this.isDraggable =
      (this.ui.position === 'Left' && this.ui.$items.length > 5) ||
      (this.ui.position === 'Center' && this.ui.$items.length > 3);
    const activeElement = this.element.querySelector('.resourceCard--active');
    if (activeElement) {
      this.ui.$activeElementIndex = Array.from(
        activeElement.parentElement.children,
      ).indexOf(activeElement);
    }
    // 1023px = widest tablet per compiled query('l', 'max')
    this.isMobile = window.matchMedia('(max-width: 1023px)').matches;
  }

  initCarousel() {
    this.flkty = new Flickity(this.ui.$carousel, {
      prevNextButtons: false,
      pageDots: false,
      cellAlign:
        this.isMobile || this.ui.position === 'Left' ? 'left' : 'center',
      draggable: this.isMobile ? true : this.isDraggable ? true : false,
      initialIndex: this.ui.$activeElementIndex ?
        this.ui.$activeElementIndex :
        0,
      contain: this.isMobile || this.ui.position === 'Left' ? true : false,
    });

    this.flkty.on(
      'dragStart',
      () => (this.flkty.slider.style.pointerEvents = 'none'),
    );
    this.flkty.on(
      'dragEnd',
      () => (this.flkty.slider.style.pointerEvents = 'auto'),
    );
  }

  bindEvents() {
    this.ui.$arrows.forEach(($arrow) => {
      $arrow.addEventListener('click', this.toggle.bind(this));
    });

    this.flkty.on('select', this.onSelect.bind(this));
  }

  toggle(e) {
    const target = e.currentTarget;
    if (!target.matches('[data-ref="carousel-arrow"]')) return;

    const direction = target.getAttribute('data-direction');

    if (direction === 'next') {
      this.flkty.next(false, false);
    } else {
      this.flkty.previous(false, false);
    }
  }

  onSelect() {
    /* eslint-disable  no-negated-condition */
    if (!this.flkty.cells[this.flkty.selectedIndex - 1]) {
      this.ui.$arrowPrev?.setAttribute('disabled', 'disabled');
      this.ui.$arrowNext?.removeAttribute('disabled');
    } else if (!this.flkty.cells[this.flkty.selectedIndex + 1]) {
      this.ui.$arrowNext?.setAttribute('disabled', 'disabled');
      this.ui.$arrowPrev?.removeAttribute('disabled');
    } else {
      this.ui.$arrowPrev?.removeAttribute('disabled');
      this.ui.$arrowNext?.removeAttribute('disabled');
    }
  }
}

registerModule('Carousel', Carousel);

// Timing constants for CarouselMedia resize retry strategy
const CAROUSEL_MEDIA_RESIZE_DELAY_FALLBACK = 300; // Fallback delay for edge cases (ms)

// Browser-specific debounce delays for CarouselMedia
const CAROUSEL_MEDIA_DEBOUNCE_DELAY_FAST = 50; // Chrome and modern browsers (ms)
const CAROUSEL_MEDIA_DEBOUNCE_DELAY_SLOW = 400; // Firefox and Safari (ms)

class CarouselMedia {
  constructor(element) {
    this.element = element;
    this.initialize();
  }

  async initialize() {
    this.bindUI();
    this.hasRevealed = false;
    this.revealPending = false;
    this.isIntersecting = false;

    // Initialize Flickity immediately - it will handle image loading with imagesLoaded: true
    this.initCarousel();

    if (this.ui.$items.length > 0) {
      this.bindEvents();
    }

    this.revealWhenReady();

    // Use Intersection Observer to handle off-screen carousels (Safari issue)
    // Safari can't calculate heights properly for elements not yet rendered in viewport
    if ('IntersectionObserver' in window) {
      this.intersectionObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            this.isIntersecting = entry.isIntersecting || entry.intersectionRatio > 0;

            if (this.isIntersecting) {
              if (!this.hasRevealed) {
                if (!this.revealPending) {
                  this.revealWhenReady();
                }
              } else {
                this.remeasureCells();
                if (this.intersectionObserver) {
                  this.intersectionObserver.disconnect();
                }
              }
            }
          });
        },
        {
          rootMargin: '50px', // Trigger slightly before entering viewport
          threshold: 0,
        },
      );
      this.intersectionObserver.observe(this.ui.$carousel);
    }
  }

  unmount() {
    if (this.flkty) {
      this.flkty.destroy();
    }

    if (this.intersectionObserver) {
      this.intersectionObserver.disconnect();
    }

    if (this.domObserver) {
      this.domObserver.disconnect();
    }

    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
  }

  bindUI() {
    this.ui = {};
    this.ui.$carousel = this.element.querySelector(
      '[data-ref="carousel-wrapper"]',
    );
    this.ui.$items = this.ui.$carousel.querySelectorAll(
      '[data-ref="carousel-item"]',
    );
    this.ui.$arrows = [
      ...this.element.querySelectorAll('[data-ref="carousel-arrow"]'),
    ];
    this.ui.$arrowPrev = this.element.querySelector('[data-direction="prev"]');
    this.ui.$arrowNext = this.element.querySelector('[data-direction="next"]');
    // 1023px = widest tablet per compiled query('l', 'max')
    this.isMobile = window.matchMedia('(max-width: 1023px)').matches;
  }

  initCarousel() {
    this.flkty = new Flickity(this.ui.$carousel, {
      imagesLoaded: true,
      prevNextButtons: false,
      pageDots: false,
      cellAlign: 'left',
      draggable: this.isMobile ? true : false,
      contain: true,
    });
  }

  bindEvents() {
    this.ui.$arrows.forEach(($arrow) => {
      $arrow.addEventListener('click', this.toggle.bind(this));
    });

    this.flkty.on('select', this.onSelect.bind(this));
    window.addEventListener('resize', this.resize.bind(this));

    // Use ResizeObserver for more accurate resize detection
    if (window.ResizeObserver) {
      this.resizeObserver = new ResizeObserver(() => {
        this.resize();
      });
      this.resizeObserver.observe(this.ui.$carousel);
    }

    // Keep MutationObserver for DOM changes
    const observer = new MutationObserver(() => {
      this.resize();
    });

    observer.observe(this.ui.$carousel, {
      childList: true, // watch for added/removed direct children
      subtree: true, // watch all descendants too
      characterData: true, // watch for changes to text nodes
    });

    this.domObserver = observer;
  }

  toggle(e) {
    const target = e.currentTarget;
    if (!target.matches('[data-ref="carousel-arrow"]')) return;

    const direction = target.getAttribute('data-direction');

    if (direction === 'next') {
      this.flkty.next(false, false);
    } else {
      this.flkty.previous(false, false);
    }
  }

  getBrowserSpecificDelay() {
    const userAgent = navigator.userAgent.toLowerCase();
    const isFirefox = userAgent.includes('firefox');
    const isSafari = userAgent.includes('safari') && !userAgent.includes('chrome');

    // Firefox and Safari need significantly more time for layout calculations
    if (isFirefox || isSafari) {
      return CAROUSEL_MEDIA_DEBOUNCE_DELAY_SLOW;
    }
    return CAROUSEL_MEDIA_DEBOUNCE_DELAY_FAST;
  }

  applyEqualHeights() {
    if (!this.flkty) {
      return false;
    }

    this.flkty.resize();
    this.ui.$carousel.classList.add('flickity-ready');
    this.ui.$carousel.classList.add('flickity-resize');
    return true;
  }

  remeasureCells() {
    if (!this.flkty) {
      return false;
    }

    if (this.ui.$carousel.classList.contains('flickity-resize')) {
      this.ui.$carousel.classList.remove('flickity-resize');
    }

    this.flkty.resize();
    this.ui.$carousel.classList.add('flickity-resize');
    return true;
  }

  revealWhenReady() {
    if (this.hasRevealed || this.revealPending) {
      return;
    }

    this.revealPending = true;

    if (this.revealFallbackTimeout) {
      clearTimeout(this.revealFallbackTimeout);
    }

    const reveal = () => {
      if (this.hasRevealed) {
        this.revealPending = false;
        return;
      }

      this.revealPending = false;

      if (this.applyEqualHeights()) {
        this.hasRevealed = true;

        if (this.isIntersecting && this.intersectionObserver) {
          this.intersectionObserver.disconnect();
        }
      }
    };

    this.revealFallbackTimeout = setTimeout(reveal, CAROUSEL_MEDIA_RESIZE_DELAY_FALLBACK);

    const images = [...this.ui.$carousel.querySelectorAll('img')];
    const incompleteImages = images.filter((img) => !img.complete);

    if (incompleteImages.length === 0) {
      clearTimeout(this.revealFallbackTimeout);
      reveal();
      return;
    }

    let pending = incompleteImages.length;

    const tryReveal = () => {
      pending -= 1;
      if (pending <= 0 && !this.hasRevealed) {
        clearTimeout(this.revealFallbackTimeout);
        reveal();
      }
    };

    incompleteImages.forEach((img) => {
      img.addEventListener('load', tryReveal, {once: true});
      img.addEventListener('error', tryReveal, {once: true});
    });
  }

  resize() {
    if (!this.hasRevealed) {
      return;
    }

    // Debounce resize calls
    if (this.resizeTimeout) {
      clearTimeout(this.resizeTimeout);
    }

    const delay = this.getBrowserSpecificDelay();

    this.resizeTimeout = setTimeout(() => {
      this.remeasureCells();
    }, delay);
  }

  onSelect() {
    /* eslint-disable  no-negated-condition */
    if (!this.flkty.cells[this.flkty.selectedIndex - 1]) {
      if (this.ui.$arrowPrev) {
        this.ui.$arrowPrev.setAttribute('disabled', 'disabled');
      }
      if (this.ui.$arrowNext) {
        this.ui.$arrowNext.removeAttribute('disabled');
      }
    } else if (!this.flkty.cells[this.flkty.selectedIndex + 1]) {
      if (this.ui.$arrowNext) {
        this.ui.$arrowNext.setAttribute('disabled', 'disabled');
      }
      if (this.ui.$arrowPrev) {
        this.ui.$arrowPrev.removeAttribute('disabled');
      }
    } else {
      if (this.ui.$arrowPrev) {
        this.ui.$arrowPrev.removeAttribute('disabled');
      }
      if (this.ui.$arrowNext) {
        this.ui.$arrowNext.removeAttribute('disabled');
      }
    }
  }
}

registerModule('CarouselMedia', CarouselMedia);
